import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ILocationModel } from 'shared/helpers/location-helper';
import { memoizeGetESFProcessEquipment, memoizeLocation } from 'shared/helpers/memoize-getters/memoize-getters';
import { getCycleDays } from 'shared/helpers/pregnancy-helper/generel-pregnancy-helpers';
import { calculateAnimalKind, calculateLitter } from 'shared/helpers/stemanimal-helper/stemanimal-helper';
import { getAnimalsOnLocation } from 'shared/helpers/work-list-helpers/esf-list-helpers/all-animals-esf-helpers';
import { GetFilteredEsfLocations } from 'shared/helpers/work-list-helpers/esf-list-helpers/general-work-list-esf-helpers';
import { localized } from 'shared/state/i18n/i18n';
import { WebAppState } from 'web/state/store.web';
import SkioldTableGrid from '../../skiold-components/skiold-table/skiold-table-grid/skiold-table-grid';
import './resend-esf-list-table.scss';
import { ViewWeb } from '../../utils/web-view';
import { TextWeb } from 'web/web-helpers/styled-text-components';
import SkioldStableSectionPicker from '../../location/location-picker/skiold-stable-section-picker';
import SkioldFormPenPicker from '../../location/location-picker/skiold-form-pen-picker';
import { checkSectionWorkLists } from 'shared/helpers/work-list-helpers/move-to-helpers/moving-lists-helper';
import { getStationsInUse } from 'shared/helpers/work-list-helpers/esf-list-helpers/generel-esf-list-helper';
import { SkioldTouchableOpacity } from '../../skiold-components/skiold-touchable-opacity';
import { SkioldImage } from '../../utils/svg/skiold-image';
import { WhiteText } from '../../Text/white-text';
import sendDataGrey from 'shared/assets/src-assets/png/send_data_grey.png';
import { ResendUpdateCycleDays } from 'shared/state/ducks/esf-cloud-to-device-messages/operations';
interface PropsFromParent {
	onSummaryChanged: (element: React.JSX.Element) => void;
}

export const ResendCycleDays: FC<PropsFromParent> = React.memo(({ onSummaryChanged }) => {
	const dispatch = useDispatch();
	// State
	const [sectionId, setSectionId] = useState<string>('');
	const [penId, setPenId] = useState<string | undefined>('');
	const [stations, setStations] = useState<string[]>([]);
	const [usesPens, setUsesPens] = useState<boolean>(false);
	const [selected, setSelected] = useState<any[]>([]);
	const skioldRef = useRef<any>(null);

	// redux selectors
	const locations = useSelector((state: WebAppState) =>
		memoizeLocation(
			state.locations.buildings,
			state.locations.sections,
			state.locations.pens,
			state.locations.valves,
		),
	);

	const propStations = useSelector((state: WebAppState) => state.station.data);
	const moveEvents = useSelector((state: WebAppState) => state.moveEvents.entities);
	const unitToPens = useSelector((state: WebAppState) => state.unitToPenData.data);
	const processEquipmentData = useSelector((state: WebAppState) =>
		memoizeGetESFProcessEquipment(state.processEquipmentData.entities),
	);
	const siteId = useSelector((state: WebAppState) => state.profile.active && state.profile.active.siteId);

	const stemAnimals = useSelector((state: WebAppState) => state.stemAnimals.entities);

	const filteredLocations = useMemo(() => {
		return GetFilteredEsfLocations(locations, processEquipmentData, unitToPens);
	}, [sectionId, locations, unitToPens]);

	const esfLocationsWithSettedPens = useMemo(() => {
		return filteredLocations.esfLocationsWithSettedPens;
	}, [filteredLocations]);

	const data = useMemo(() => {
		let data: any[] = [];
		if (!sectionId) {
			return data;
		}
		const esfLocations = sectionId
			? esfLocationsWithSettedPens
					.filter(esfLoc => esfLoc.sectionId === sectionId && esfLoc.id !== undefined)
					.map(esfLocWithSettedPens => esfLocWithSettedPens.id)
			: esfLocationsWithSettedPens.map(esfLocWithSettedPens => esfLocWithSettedPens.id);
		let utps = unitToPens.filter(utp => esfLocations.includes(utp.processEquipmentDataId));
		let animalIds = getAnimalsOnLocation(
			utps.map(utp => utp.penId!),
			moveEvents,
			stemAnimals,
		);
		const animals = stemAnimals.filter(stem => animalIds.includes(stem.id));
		const today = new Date();
		animals.forEach(animal => {
			data.push({
				stemAnimalId: animal.id,
				animalNumber: animal.animalNumber,
				litter: calculateLitter(animal, today),
				cycleDays: getCycleDays(animal.id!),
				siteId: siteId,
				animalKind: localized(calculateAnimalKind(animal)),
			});
		});

		return data;
	}, [stemAnimals, sectionId, esfLocationsWithSettedPens, unitToPens, moveEvents]);
	// Set reesend
	useEffect(() => {
		const resend = () => {
			dispatch(ResendUpdateCycleDays(selected.map(x => x.stemAnimalId)));
			if(skioldRef.current) {
				skioldRef.current.ResetSelected([]);
			}
			
		};

		onSummaryChanged(
			<ViewWeb className={'centerResendIcon'}>
				<SkioldTouchableOpacity onPress={resend}>
					<SkioldImage width="60" height="60" imageData={sendDataGrey} />
				</SkioldTouchableOpacity>
				<WhiteText>{localized('sendMarked')}</WhiteText>
			</ViewWeb>,
		);
	}, [selected]);

	const columns = useMemo(() => {
		return [
			{
				name: 'animalNumber',
				title: localized('animalNr'),
			},
			{
				name: 'litter',
				title: localized('litter'),
			},
			{
				name: 'cycleDays',
				title: localized('cycleDays'),
			},
			{
				name: 'animalKind',
				title: localized('kind'),
			},
		];
	}, []);

	const columnExtensions = useMemo(() => {
		return [
			{
				columnName: 'animalNumber',
				width: 80,
			},
			{
				columnName: 'litter',
				width: 80,
			},
			{
				columnName: 'cycleDays',
				width: 80,
			},
			{
				columnName: 'animalKind',
				width: 80,
			},
		];
	}, []);

	const onPenChanged = useCallback(penId => {
		setPenId(penId);
	}, []);

	const onStableSectionChanged = useCallback(
		sectionId => {
			let sectionInfo = checkSectionWorkLists(sectionId, penId);
			let stationsStrings = getStationsInUse(unitToPens, sectionId, propStations, esfLocationsWithSettedPens);

			setPenId(undefined);
			setSectionId(sectionId);
			setStations(stationsStrings);
			setUsesPens(sectionInfo.usesPens);
		},
		[penId],
	);

	// const setRef = useCallback((s: any) => {
	// 	if (s) {
	// 		SkioldTableRef = s;
	// 	}
	// }, []);

	return (
		<ViewWeb className="resend-cycle-days">
			<ViewWeb className="z-index-1000">
				<ViewWeb className="getrows">
					<ViewWeb className="flex-direction-row">
						<ViewWeb className="flexFour flex-direction-row ">
							<ViewWeb className="buttons-no-right-border">
								<TextWeb className="stable-section-text">{localized('chooseLocation')}:</TextWeb>
								<SkioldStableSectionPicker
									onStableSectionSelected={onStableSectionChanged}
									sectionId={sectionId}
									filteredLocations={filteredLocations.locations}
									isPigProduction={true}
									containerClassName="picker-width"
								/>
								{usesPens && (
									<ViewWeb className="buttons-no-right-border">
										<TextWeb className="pen-text">{localized('Pen')}:</TextWeb>
										<SkioldFormPenPicker
											onPenSelected={onPenChanged}
											filteredLocations={filteredLocations.locations}
											sectionId={sectionId}
											penId={undefined}
											theme="light"
											containerClassName="picker-width"
											usedInsideForm={false}
										/>
									</ViewWeb>
								)}
							</ViewWeb>
						</ViewWeb>
					</ViewWeb>
					<ViewWeb className="flex-direction-row">
						<ViewWeb className="flexFour flex-direction-row ">
							<ViewWeb className="buttons-no-right-border">
								<TextWeb className="stable-section-text">{localized('Stations')}:</TextWeb>
								<TextWeb className="stable-section-text">{stations}</TextWeb>
							</ViewWeb>
						</ViewWeb>
					</ViewWeb>
				</ViewWeb>
			</ViewWeb>
			<SkioldTableGrid
				ref={skioldRef}
				columns={columns}
				tableKey={'resend-cycle-days'}
				data={data ?? []}
				ignoreSetCount={true}
				ColumnExtensions={columnExtensions}
				useSelection={true}
				selectedChanged={setSelected}
				selectionHeader={localized('completed')}
			/>
		</ViewWeb>
	);
});
